import { Pipe, type PipeTransform, inject } from '@angular/core';

import { resolveTranslation, type SupportedLanguage, type TranslatableText } from '@zag/core/common';

import { LanguageService } from '../services/language.service';

/**
 * Read-only template pipe that resolves a {@link TranslatableText} value to
 * a single string using the canonical fallback chain. Honours the user's
 * current UI language by default.
 *
 * The pipe is `pure: false` so it re-evaluates when the underlying
 * translations object mutates (e.g. after a save). Since the language signal
 * is read on each invocation, language switches also propagate.
 *
 * @example
 * ```html
 * <span>{{ entity.nameTranslations | zagTranslated:'name' }}</span>
 * <span>{{ entity.descriptionTranslations | zagTranslated:'description':legacyDescription }}</span>
 * <span>{{ entity.nameTranslations | zagTranslated:'name':null:'de' }}</span>
 * ```
 */
@Pipe({
  name: 'zagTranslated',
  pure: false,
})
export class TranslatedPipe implements PipeTransform {
  private readonly languageService = inject(LanguageService);

  transform<Fields extends string>(
    value: TranslatableText<Fields> | null | undefined,
    field: Fields,
    legacy?: string | null,
    language?: SupportedLanguage
  ): string | null {
    const lang = language ?? this.languageService.getCurrentLanguage();
    return resolveTranslation(value, field, lang, { legacy });
  }
}
